import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Menu, X, Lock, Heart, Brain } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useVisitor } from "../context/VisitorAuthContext";
import { AdUnit } from "./AdUnit";
import { AD_SLOTS } from "../lib/adsConfig";

const Header = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { visitor, openAuth, logout: visitorLogout } = useVisitor();

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-[#FAF7F0]/95 backdrop-blur-md border-b border-[#D8CEB9]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link
            to="/"
            data-testid="site-logo-link"
            className="flex items-center gap-2.5 shrink-0 group"
          >
            <img
              src="/seal-header.png"
              alt="CurioVault red wax seal"
              width={44}
              height={44}
              data-testid="header-seal"
              className="w-11 h-11 object-contain drop-shadow-sm transition-transform group-hover:scale-105 group-hover:-rotate-6"
            />
            <span className="font-serif-display text-xl sm:text-2xl font-bold tracking-tight text-[#1A1615] leading-none">
              CurioVault
            </span>
          </Link>

          {/* Desktop search */}
          <form
            onSubmit={submit}
            className="hidden md:flex flex-1 max-w-md items-center relative"
          >
            <Search className="absolute left-3 w-4 h-4 text-[#7A6C5D]" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the vault…"
              className="w-full font-ui text-sm pl-9 pr-3 py-2 bg-white border border-[#D8CEB9] rounded-full outline-none focus:border-[#9E4535] focus:ring-2 focus:ring-[#F5E8EA] transition"
            />
          </form>

          <nav className="hidden md:flex items-center gap-6 font-ui text-sm">
            <Link to="/#subjects" data-testid="nav-subjects" className="text-[#4A4036] hover:text-[#9E4535] transition-colors">
              Subjects
            </Link>
            <Link to="/trivia" data-testid="nav-trivia" className="text-[#4A4036] hover:text-[#9E4535] transition-colors">
              Trivia Vault
            </Link>
            {visitor ? (
              <>
                <Link to="/favourites" data-testid="nav-favourites" className="flex items-center gap-1.5 text-[#4A4036] hover:text-[#9E4535] transition-colors">
                  <Heart className="w-3.5 h-3.5" /> Favourites
                </Link>
                <button onClick={visitorLogout} data-testid="nav-visitor-logout" className="text-[#7A6C5D] hover:text-[#9E4535] transition-colors">
                  Sign out
                </button>
              </>
            ) : (
              <button onClick={openAuth} data-testid="nav-signin" className="text-[#4A4036] hover:text-[#9E4535] transition-colors">
                Sign in
              </button>
            )}
            {user ? (
              <>
                <Link to="/admin/curios" data-testid="nav-admin-facts" className="text-[#4A4036] hover:text-[#9E4535] transition-colors">
                  Manage Curios
                </Link>
                <Link to="/admin/new" data-testid="nav-admin-new" className="text-[#4A4036] hover:text-[#9E4535] transition-colors">
                  Add Curio
                </Link>
                <button onClick={logout} data-testid="nav-logout" className="text-[#7A6C5D] hover:text-[#9E4535] transition-colors">
                  Log out
                </button>
              </>
            ) : (
              <Link
                to="/admin"
                data-testid="nav-admin"
                className="flex items-center gap-1.5 text-[#4A4036] hover:text-[#9E4535] transition-colors"
              >
                <Lock className="w-3.5 h-3.5" /> Curator
              </Link>
            )}
          </nav>

          <button
            className="md:hidden text-[#1A1615]"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile panel */}
        {open && (
          <div className="md:hidden pb-4 space-y-3 cv-fade-up">
            <form onSubmit={submit} className="flex items-center relative">
              <Search className="absolute left-3 w-4 h-4 text-[#7A6C5D]" />
              <input
                data-testid="mobile-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search the vault…"
                className="w-full font-ui text-sm pl-9 pr-3 py-2 bg-white border border-[#D8CEB9] rounded-full outline-none focus:border-[#9E4535]"
              />
            </form>
            <div className="flex flex-col gap-2 font-ui text-sm">
              <Link to="/" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Home</Link>
              <Link to="/#subjects" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Browse Subjects</Link>
              <Link to="/trivia" data-testid="mobile-nav-trivia" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Trivia Vault</Link>
              <Link to="/about" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">About Us</Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Contact Us</Link>
              <Link to="/copyright-policy" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Copyright Policy</Link>
              <Link to="/privacy" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Privacy Policy</Link>

              <div className="border-t border-[#E6DCC8] my-1" />
              {visitor ? (
                <>
                  <Link to="/favourites" onClick={() => setOpen(false)} className="py-1 flex items-center gap-1.5 text-[#4A4036]"><Heart className="w-3.5 h-3.5" /> Favourites</Link>
                  <button onClick={() => { visitorLogout(); setOpen(false); }} className="py-1 text-left text-[#7A6C5D]">Sign out</button>
                </>
              ) : (
                <button onClick={() => { openAuth(); setOpen(false); }} className="py-1 text-left text-[#4A4036]">Sign in</button>
              )}
              {user ? (
                <>
                  <Link to="/admin/curios" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Manage Curios</Link>
                  <Link to="/admin/new" onClick={() => setOpen(false)} className="py-1 text-[#4A4036]">Add Curio</Link>
                  <button onClick={() => { logout(); setOpen(false); }} className="py-1 text-left text-[#7A6C5D]">Log out</button>
                </>
              ) : (
                <Link to="/admin" onClick={() => setOpen(false)} className="py-1 flex items-center gap-1.5 text-[#4A4036]"><Lock className="w-3.5 h-3.5" /> Curator Access</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

const FOOTER_LINKS = [
  ["/", "Home"], ["/#subjects", "Subjects"], ["/trivia", "Trivia Vault"], ["/about", "About"],
  ["/editorial-standards", "Editorial Standards"], ["/contact", "Contact"], ["/copyright-policy", "Copyright"],
  ["/privacy", "Privacy"], ["/admin", "Curator"],
];

const Footer = () => (
  <footer className="bg-[#fdf6e3] border-t border-[#e8dcc3] py-12 px-6 mt-20" data-testid="site-footer">
    <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
      <img
        src="/seal-curiovault-red.png"
        alt="CurioVault red wax seal with a magnifying glass at 45 degrees"
        className="w-28 h-28 mb-4 drop-shadow-sm"
        width={112}
        height={112}
        loading="lazy"
        data-testid="footer-seal"
      />
      <p className="font-serif-display text-[11px] tracking-[0.2em] uppercase text-[#5c4a2e] mb-2">
        CurioVault.co • An OddlyTrue Publishing Imprint
      </p>
      <p className="font-body text-[12px] text-[#8a7a5e] max-w-md">
        Victorian museum chart • Daily historical truths • Est. 2024
      </p>
      <nav className="mt-7 flex flex-wrap justify-center gap-x-5 gap-y-2 font-ui text-[12px] text-[#6b5a3e]" data-testid="footer-nav">
        {FOOTER_LINKS.map(([to, label]) => (
          <Link key={to} to={to} data-testid={to === "/editorial-standards" ? "footer-editorial-standards" : undefined}
            className="hover:text-[#9E4535] transition-colors">{label}</Link>
        ))}
      </nav>
      <div className="mt-6 flex gap-6 text-[10px] tracking-widest uppercase text-[#9ca3af]" data-testid="footer-sister-sites">
        <a href="https://oddlytrue.co" className="hover:text-[#5c4a2e] transition-colors">OddlyTrue.co</a>
        <span>•</span>
        <a href="https://astralogico.com" className="hover:text-[#5c4a2e] transition-colors">AstraLogico.com</a>
      </div>
      <p className="mt-6 font-mono-cv text-[10px] text-[#a89878]">© {new Date().getFullYear()} CurioVault</p>
    </div>
  </footer>
);

export const Layout = ({ children, ads = false }) => {
  const { pathname } = useLocation();
  // AdSense policy: no ads on admin/login screens (no publisher content).
  const showAds = ads && !pathname.startsWith("/admin");
  return (
    <div className="App paper-texture min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      {showAds && (
        <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8">
          <AdUnit slot={AD_SLOTS.footerLeaderboard} minHeight={90} testid="ad-footer-leaderboard" />
        </div>
      )}
      <Footer />
    </div>
  );
};
